import { LockOutlined, MailOutlined, UserOutlined } from "@ant-design/icons";
import { Alert, Button, Card, Form, Input, Typography } from "antd";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useT } from "@trust/i18n";
import { http } from "@/lib/api";
import { useAuthStore, type UserInfo } from "@/lib/auth";

interface RegisterResp {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

interface RegisterForm {
  username: string;
  password: string;
  confirm: string;
  email?: string;
}

export function RegisterPage() {
  const t = useT();
  const navigate = useNavigate();
  const { setTokens, setUser, loggedIn } = useAuthStore();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  if (loggedIn) {
    navigate({ to: "/" });
    return null;
  }

  async function onFinish(values: RegisterForm) {
    setError(null);
    setLoading(true);
    try {
      const tokens = await http<RegisterResp>("/auth/register", {
        method: "POST",
        body: {
          username: values.username,
          password: values.password,
          email: values.email || undefined,
        },
      });
      setTokens(tokens.access_token, tokens.refresh_token);
      const me = await http<UserInfo>("/auth/me");
      setUser(me);
      navigate({ to: "/" });
    } catch (exc) {
      setError((exc as Error).message || t("auth.registerFailed"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <Card style={{ width: 380, maxWidth: "100%" }}>
      <div style={{ textAlign: "center", marginBottom: 24 }}>
        <Typography.Title level={3} style={{ marginBottom: 4 }}>
          {t("auth.registerTitle")}
        </Typography.Title>
        <Typography.Text type="secondary">{t("auth.loginSubtitle")}</Typography.Text>
      </div>

      {error && (
        <Alert
          type="error"
          showIcon
          message={error}
          style={{ marginBottom: 16 }}
          closable
          onClose={() => setError(null)}
        />
      )}

      <Form layout="vertical" onFinish={onFinish} requiredMark={false}>
        <Form.Item
          label={t("auth.username")}
          name="username"
          rules={[
            { required: true, message: t("auth.usernameRequired") },
            { pattern: /^[A-Za-z0-9_\-.]{2,64}$/, message: t("auth.usernamePlaceholder") },
          ]}
        >
          <Input prefix={<UserOutlined />} placeholder={t("auth.usernamePlaceholder")} />
        </Form.Item>
        <Form.Item
          label={t("auth.email")}
          name="email"
          rules={[{ type: "email", message: t("auth.emailInvalid") }]}
        >
          <Input prefix={<MailOutlined />} placeholder={t("auth.emailPlaceholder")} />
        </Form.Item>
        <Form.Item
          label={t("auth.password")}
          name="password"
          rules={[
            { required: true, message: t("auth.passwordRequired") },
            { min: 8, message: t("auth.newPasswordPlaceholder") },
            {
              pattern: /^(?=[\s\S]*\p{L})(?=[\s\S]*\p{Nd})/u,
              message: t("auth.newPasswordPlaceholder"),
            },
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder={t("auth.newPasswordPlaceholder")}
          />
        </Form.Item>
        <Form.Item
          label={t("auth.confirmPassword")}
          name="confirm"
          dependencies={["password"]}
          rules={[
            { required: true, message: t("auth.confirmPasswordRequired") },
            ({ getFieldValue }) => ({
              validator(_, value: string) {
                if (!value || getFieldValue("password") === value) return Promise.resolve();
                return Promise.reject(new Error(t("auth.confirmPasswordMismatch")));
              },
            }),
          ]}
        >
          <Input.Password prefix={<LockOutlined />} placeholder={t("auth.confirmPassword")} />
        </Form.Item>
        <Button type="primary" htmlType="submit" block loading={loading}>
          {t("auth.registerButton")}
        </Button>
      </Form>

      <div style={{ marginTop: 16, textAlign: "center" }}>
        <Link to="/inx/login">{t("auth.backToLogin")}</Link>
      </div>
    </Card>
  );
}
